// Route: /
//
// The hero, the numbers (interpolated from stats.json — never hand-typed), the generated
// signal spine, and the doors — one per audience, plus the hands-on tour, recipes and embed docs.

import { mountShell, section, body, el, href, loadGenerated, REPO } from '../shell.js';
import '../demos/spine.js';
import { attractMonitor } from '../demos/attract.js';

interface Stats {
  scenarios: { authored: number; executed: number };
  steps: { authored: number; executed: number };
  featureFiles: number;
  adrs: number;
}

const main = mountShell({ route: '' });

// ---------------------------------------------------------------- hero

const hero = el('section');
hero.style.padding = '72px 0 20px';
const heroWrap = el('div', { class: 'mx-wrap' });
heroWrap.innerHTML = `
  <span class="mx-label">Proof of concept · WebGPU · vanilla TypeScript</span>
  <h1 style="margin:14px 0 18px;max-width:20ch">A 1990s broadcast mixer, rebuilt in the browser.</h1>
  <p class="mx-prose" style="font-size:18px;color:var(--mx-label);max-width:60ch">
    <strong style="color:var(--mx-text)">web-mx-50</strong> reproduces the Panasonic WJ-MX50 two-bus
    digital A/V mixer block for block — one pure state store, an explicit signal graph, and a WebGPU
    renderer, with no framework and no bundler. Behaviour is pinned by
    <span data-executed>executable</span> specs derived from the manual.
  </p>
  <p class="mx-prose" style="color:var(--mx-label);max-width:60ch">
    Here is every block, running, next to the spec that pins it.
  </p>
  <div class="mx-row" style="margin-top:26px;gap:10px">
    <a class="mx-cta" href="${href('console/')}" style="padding:11px 18px"><span class="led"></span>Launch the console</a>
    <a class="mx-btn" href="${href('learn/')}" style="padding:11px 16px;text-decoration:none">Learn the panel in six steps</a>
    <a class="mx-btn" href="${href('architecture/')}" style="padding:11px 16px;text-decoration:none">Read how it's built</a>
  </div>`;
heroWrap.appendChild(attractMonitor());
hero.appendChild(heroWrap);
main.appendChild(hero);

// ---------------------------------------------------------------- numbers

const numbers = section({});
const numbersBody = body(numbers);
const strip = el('div', { class: 'mx-grid cols-3' });
strip.style.gridTemplateColumns = 'repeat(4, minmax(0,1fr))';
numbersBody.appendChild(strip);
const note = el('p', { class: 'mx-dim' });
note.style.cssText = 'margin:16px 0 0;font-size:13px;max-width:70ch';
numbersBody.appendChild(note);
main.appendChild(numbers);

function stat(value: string, label: string, sub: string, link: string): HTMLElement {
  const a = el('a', { attrs: { href: link } });
  a.className = 'mx-panel mx-card';
  a.style.cssText = 'text-decoration:none;display:block';
  a.innerHTML =
    `<div class="mx-readout big" style="display:block;text-align:center">${value}</div>` +
    `<div class="mx-label" style="text-align:center;margin-top:10px">${label}</div>` +
    `<div class="mx-dim" style="text-align:center;font-size:12px;margin-top:4px">${sub}</div>`;
  return a;
}

void loadGenerated<Stats>('stats.json').then((stats) => {
  if (!stats) {
    strip.innerHTML = `<p class="mx-dim">Counts unavailable — run <code>npm run site:data</code>.</p>`;
    return;
  }
  const FEATURES = REPO + '/tree/main/features';
  const ADRS = REPO + '/tree/main/adr';
  strip.appendChild(stat(String(stats.scenarios.executed), 'scenarios execute', `of ${stats.scenarios.authored} authored`, FEATURES));
  strip.appendChild(stat(String(stats.steps.executed), 'steps execute', `of ${stats.steps.authored} authored`, FEATURES));
  strip.appendChild(stat(String(stats.featureFiles), 'feature files', 'the living specification', FEATURES));
  strip.appendChild(stat(String(stats.adrs), 'decisions recorded', 'in the repository', ADRS));

  const marker = heroWrap.querySelector('[data-executed]');
  if (marker) marker.textContent = String(stats.scenarios.executed);

  note.innerHTML =
    `Every figure is generated by the CI run, never typed into a page — and the split is deliberate: ` +
    `<strong>${stats.scenarios.executed}</strong> scenarios execute headlessly in the gate, while ` +
    `<strong>${stats.scenarios.authored - stats.scenarios.executed}</strong> more are authored and deliberately ` +
    `deferred (interlace behaviour with no analogue in a progressive build, and pattern-table data that isn't ` +
    `derivable from the reference). Which is which is recorded in ` +
    `<a href="${REPO}/blob/main/docs/DEFERRED.md">DEFERRED.md</a>. ` +
    `There is no unit-test count here on purpose: it moves with every commit and tells you nothing.`;
});

// ---------------------------------------------------------------- spine

const spine = section({
  label: 'The signal path is the site',
  title: 'One fixed block order, structural in the code',
});
const spineBody = body(spine);
spineBody.appendChild(
  el('p', {
    class: 'mx-prose',
    html:
      `The hardware has one fixed chain, and so does the recreation. It is this project's table of contents: ` +
      `every description hangs off a stage, and the stage order below is read out of ` +
      `<code>src/core/signal-graph.ts</code> at page load, so this picture cannot drift from the pipeline.`,
  }),
);
spineBody.appendChild(document.createElement('mx-demo-spine'));
const offSpine = el('p', {
  class: 'mx-prose',
  html:
    `Four parts of the machine sit <em>off</em> that path — the 7-input audio mixer with Audio Follow and ` +
    `A/V Synchro, Event Memory, Special Modes, and the input/control layer. They get ` +
    `<a href="${href('machine/audio-memory-control/')}">their own page</a>, not a footnote: a site that files ` +
    `everything under a video stage quietly drops the "A" of A/V mixer.`,
});
offSpine.style.marginTop = '22px';
spineBody.appendChild(offSpine);
main.appendChild(spine);

// ---------------------------------------------------------------- doors

const doors = section({ label: 'Ways in', title: 'Pick the thing you came for' });
const doorsBody = body(doors);
const doorGrid = el('div', { class: 'mx-grid cols-3' });

function door(kicker: string, title: string, blurb: string, link: string, cta: string): HTMLElement {
  const a = el('a', { attrs: { href: link } });
  a.className = 'mx-panel mx-card';
  a.style.cssText = 'text-decoration:none;display:block';
  a.innerHTML =
    `<span class="mx-label">${kicker}</span>` +
    `<h3 style="margin:10px 0 8px">${title}</h3>` +
    `<p class="mx-dim" style="margin:0 0 14px">${blurb}</p>` +
    `<span style="color:var(--mx-amber);font-size:13px">${cta} &rarr;</span>`;
  return a;
}

doorGrid.appendChild(
  door(
    'For video people',
    'The Machine',
    'Did they get the wipes right? The DSK edges, the pattern numbers, the freeze family? Every block, described and running.',
    href('machine/'),
    'Open the blocks',
  ),
);
doorGrid.appendChild(
  door(
    'For the curious',
    'The wipe engine',
    'Seven families composed into every pattern rather than drawn — with the RS-422 numbering oracle answering live, and the geometry the shader actually receives.',
    href('machine/wipes/'),
    'Open the deep dive',
  ),
);
doorGrid.appendChild(
  door(
    'New here',
    'Learn the panel',
    'One live mixer that gains a block per step — a source, the lever, a wipe, an effect, a title, a fade. The real renderer, nothing mocked.',
    href('learn/'),
    'Start the tour',
  ),
);
doorGrid.appendChild(
  door(
    'For the manual readers',
    'Combination recipes',
    'The signature looks from the manual, each played step by step from its Gherkin scenario, with every assertion checked on the live panel.',
    href('machine/recipes/'),
    'Play the recipes',
  ),
);
doorGrid.appendChild(
  door(
    'For people embedding it',
    'Embed it',
    'One tag, blocks à la carte, controls anywhere on the page, or script only — runnable examples, the full element reference, a playground.',
    href('embed/'),
    'Read the embed docs',
  ),
);
doorGrid.appendChild(
  door(
    'For web-platform people',
    'The build story',
    'Native ESM with zero bare imports and no import map, a WebGPU signal graph, and a fixed-timestep clock — no bundler anywhere.',
    href('architecture/'),
    'See how it is built',
  ),
);
doorsBody.appendChild(doorGrid);
main.appendChild(doors);

// ---------------------------------------------------------------- honesty

const honest = section({ label: 'What this is not', title: 'A proof of concept, stated plainly' });
const honestBody = body(honest);
honestBody.appendChild(
  el('div', {
    class: 'mx-prose',
    html:
      `<p>This is not a product, not a Panasonic emulator you can put on air, and not a finished application. ` +
      `It is an experiment in whether a dense piece of broadcast hardware can be reproduced faithfully in a ` +
      `browser — driven by one state store, rendered on WebGPU, and held in place by executable specs written ` +
      `from the manual.</p>` +
      `<p>The domain model is complete and verified headlessly. Some things are deliberately not built, and some ` +
      `cannot be verified in this environment at all — the golden-image tier skips here for want of a headless ` +
      `WebGPU adapter. The live benches on this site need a WebGPU browser; where there is none they say so ` +
      `rather than show a picture they cannot render. ` +
      `All of it is inventoried with file and line pointers in ` +
      `<a href="${REPO}/blob/main/docs/DEFERRED.md">DEFERRED.md</a>.</p>` +
      `<p class="mx-dim">Source: <a href="${REPO}">github.com/sebs/webgpu-mx-50</a> · ` +
      `<a href="${REPO}/blob/main/LICENSE">GNU AGPL v3.0</a></p>`,
  }),
);
main.appendChild(honest);
